import { enableProdMode, provideZoneChangeDetection } from '@angular/core';
import { platformBrowserDynamic } from '@angular/platform-browser-dynamic';
import * as Sentry from '@sentry/angular';
import { Capacitor } from '@capacitor/core';

import { AppModule } from './app/app.module';
import { environment } from './environments/environment';

// Initialize Sentry for web/browser
Sentry.init({
  dsn: environment.sentry.dsn,
  environment: environment.sentry.environment,
  integrations: [
    Sentry.browserTracingIntegration({
      // Don't trace interactions on these elements to reduce overhead
      shouldCreateSpanForRequest: (url) => {
        // Skip tracing for assets and frequent requests
        return !url.includes('/assets/') &&
               !url.includes('maps.googleapis.com') &&
               !url.includes('analytics');
      },
    }),
    Sentry.replayIntegration({
      maskAllText: false,
      blockAllMedia: false,
    }),
  ],
  // Performance Monitoring
  tracesSampleRate: environment.production ? 0.1 : 1.0,

  // Performance optimization: Filter out noise
  beforeSend(event, hint) {
    // Don't send errors for common browser issues
    if (event.exception) {
      const error = hint.originalException;
      if (error && typeof error === 'string') {
        // Skip network errors and common browser issues
        if (error.includes('Network Error') ||
            error.includes('ERR_NETWORK') ||
            error.includes('Script error')) {
          return null;
        }
      }
    }

    // Only send a sample of non-critical events in production
    if (environment.production && event.level === 'info') {
      return Math.random() < 0.1 ? event : null; // 10% sampling for info events
    }

    return event;
  },

  // Session Replay - Optimized for performance
  replaysSessionSampleRate: 0.05, // Reduced from 10% to 5%
  replaysOnErrorSampleRate: 0.5, // Reduced from 100% to 50% for errors
});

if (environment.production) {
  enableProdMode();
}

if (Capacitor.isNativePlatform() && 'serviceWorker' in navigator) {
  navigator.serviceWorker.getRegistrations().then((registrations) => {
    registrations.forEach((registration) => {
      registration.unregister().catch(() => {});
    });
  }).catch(() => {});

  if ('caches' in window) {
    caches.keys().then((keys) => {
      keys.forEach((key) => {
        caches.delete(key).catch(() => {});
      });
    }).catch(() => {});
  }
}

platformBrowserDynamic().bootstrapModule(AppModule, { applicationProviders: [provideZoneChangeDetection()], })
  .catch(err => console.log(err));
